import PropTypes from 'prop-types';
import React, { ElementType, ReactNode, useCallback, useState } from 'react';
import uuid from 'uuid/v4';
import { MODALS } from '../../constants';
import ModalContext from '../../contexts/modal';
import AlertModal from '../AlertModal';
import ConfirmationModal from '../ConfirmationModal';
import FormModal from '../FormModal';
import LightBox from '../LightBox';

// TODO: Add type checking to modal props
const ModalElementTypes: { [key: string]: ElementType } = {
  [MODALS.FORM_MODAL]: FormModal,
  [MODALS.LIGHT_BOX]: LightBox,
  [MODALS.CONFIRMATION_MODAL]: ConfirmationModal,
  [MODALS.ALERT_MODAL]: AlertModal
};

type ModalProviderProps = {
  children: ReactNode;
};

const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<
    { visibility: boolean; modalType: string; modalProps: any; id: string }[]
  >([]);

  const openModal = useCallback((modalType, modalProps = {}) => {
    const handleCloseModal = () => {
      if (modalProps.onClose) modalProps.onClose();
      setModals(prevModals => {
        const prevLastModal = prevModals[prevModals.length - 1];
        const disposeLastModal = prevLastModal && !prevLastModal.visibility;

        let nextModals;
        let nextLastModal;
        if (disposeLastModal) {
          nextModals = prevModals.slice(0, prevModals.length - 1);
          nextLastModal = nextModals[nextModals.length - 1];
        } else {
          nextModals = prevModals;
          nextLastModal = prevLastModal;
        }
        return [
          ...nextModals.slice(0, nextModals.length - 1),
          {
            ...nextLastModal,
            visibility: false
          }
        ];
      });
    };
    setModals(prevModals => {
      const lastModal = prevModals[prevModals.length - 1];
      const reUseLastModal = lastModal && !lastModal.visibility;
      return [
        ...(reUseLastModal
          ? prevModals.slice(0, prevModals.length - 1)
          : prevModals),
        {
          id: reUseLastModal ? lastModal.id : uuid(),
          modalType,
          visibility: true,
          modalProps: {
            ...modalProps,
            onClose: handleCloseModal
          }
        }
      ];
    });
    return handleCloseModal;
  }, []);

  return (
    <ModalContext.Provider value={{ openModal }}>
      {children}
      {modals.map(({ visibility, modalType, modalProps, id }) => {
        const ModalElementType = ModalElementTypes[modalType];
        return (
          <ModalElementType key={id} isOpen={visibility} {...modalProps} />
        );
      })}
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default ModalProvider;
